import { SerializedError, createSlice, isAnyOf } from '@reduxjs/toolkit';

import { REQUEST_METHODS } from '@lib/core/service/consts';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { parseError } from '@lib/core/service/requests/parseError';
import { request } from '@lib/core/service/requests/request';
import { selectListData } from '@lib/core/serviceTerms/selectors';
import { apiUrlServiceTerms } from '@lib/core/serviceTerms/slices/urls';
import { IParsedServiceTerm, IServiceTermsOptionsResponse } from '@lib/core/serviceTerms/types';

export interface IServiceTermsSlice {
  list: {
    data: { results: IParsedServiceTerm[] };
    error: SerializedError;
    loading: boolean;
    firstGetDataSuccessFlag: boolean;
  };
  options: { data: IServiceTermsOptionsResponse; error: SerializedError; loading: boolean };
}

const initialState: IServiceTermsSlice = {
  list: { data: { results: [] }, error: null, firstGetDataSuccessFlag: false, loading: false },
  options: { data: { choices: { service_terms: { platform: [], retailer: [] } } }, error: null, loading: false },
};

export const actionGetServiceTermsOptions = createTypedAsyncThunk('serviceTerms/options', async () =>
  request({
    method: REQUEST_METHODS.OPTIONS,
    url: apiUrlServiceTerms(),
  }),
);

export const actionGetServiceTermsList = createTypedAsyncThunk('serviceTerms/get', async () =>
  request({
    method: REQUEST_METHODS.GET,
    url: apiUrlServiceTerms(),
  }),
);

export const actionUpdateServiceTerms = createTypedAsyncThunk(
  'serviceTerms/update',
  async (serviceTerms: IParsedServiceTerm[], { getState }) => {
    try {
      return await Promise.all(
        serviceTerms.map(serviceTerm => {
          const listData = selectListData(getState());
          const isThisUserTermsServiceRegister = listData.find(
            userTerm => userTerm.identifier === serviceTerm.term || userTerm.term === serviceTerm.term,
          );

          if (isThisUserTermsServiceRegister) {
            return request({
              body: serviceTerm,
              method: REQUEST_METHODS.PUT,
              url: `${apiUrlServiceTerms()}${serviceTerm.term}/`,
            });
          }

          return request({
            body: serviceTerm,
            method: REQUEST_METHODS.POST,
            url: apiUrlServiceTerms(),
          });
        }),
      );
    } catch (error) {
      return Promise.reject(parseError(error));
    }
  },
);

export const serviceTermsSlice = createSlice({
  extraReducers: builder => {
    builder.addCase(actionGetServiceTermsList.fulfilled, (state, action) => {
      state.list.loading = false;
      state.list.data = action.payload;
      state.list.firstGetDataSuccessFlag = true;
    });

    builder.addCase(actionGetServiceTermsOptions.pending, state => {
      state.options.loading = true;
    });
    builder.addCase(actionGetServiceTermsOptions.rejected, (state, action) => {
      state.options.loading = false;
      state.options.error = action.error;
    });
    builder.addCase(actionGetServiceTermsOptions.fulfilled, (state, action) => {
      state.options.loading = false;
      state.options.data = action.payload;
    });
    builder.addCase(actionUpdateServiceTerms.pending, state => {
      state.list.loading = true;
    });
    builder.addCase(actionUpdateServiceTerms.fulfilled, (state, action) => {
      state.list.loading = false;
      const dataMap = new Map();

      state.list.data.results.forEach(item => {
        dataMap.set(item.identifier || item.term, item);
      });

      action.payload.forEach(({ term, is_selected }) => {
        dataMap.set(term.identifier, { ...term, is_selected });
      });

      state.list.data.results = Array.from(dataMap.values());
    });

    builder.addMatcher(isAnyOf(actionGetServiceTermsList.fulfilled, actionUpdateServiceTerms.fulfilled), state => {
      state.list.loading = false;
    });
    builder.addMatcher(
      isAnyOf(actionGetServiceTermsList.rejected, actionUpdateServiceTerms.rejected),
      (state, action) => {
        state.list.loading = false;
        state.list.error = action.error;
      },
    );
  },
  initialState,
  name: 'serviceTerms',
  reducers: {
    actionResetServiceTermsList: state => {
      return {
        ...state,
        list: initialState.list,
      };
    },
  },
});

export default serviceTermsSlice.reducer;
export const { actionResetServiceTermsList } = serviceTermsSlice.actions;
